import React, { useState } from 'react';
import { FileText, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, Target, Scale, Users, ChevronDown, DollarSign, Award } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ServiceHeroForm from '../../components/ServiceHeroForm';
import TrademarkPricingSection from '../../components/TrademarkPricingSection';

const TrademarkApplicationPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const benefits = [
    { icon: Shield, title: 'Legal Protection', description: 'Exclusive rights to use your mark nationwide for your goods/services' },
    { icon: Award, title: 'Brand Value', description: 'Registered trademarks increase business valuation by 20-30%' },
    { icon: Target, title: 'Market Deterrent', description: 'Stop competitors from using confusingly similar marks' },
    { icon: DollarSign, title: 'Licensing Revenue', description: 'Generate income by licensing your trademark to others' }
  ];

  const applicationProcess = [
    { step: 'Trademark Search', description: 'Comprehensive search to ensure your mark is available', duration: '3-5 days', critical: true },
    { step: 'Class Selection', description: 'Identify correct international classes for your goods/services', duration: '1-2 days', critical: true },
    { step: 'Description Drafting', description: 'Precise description meeting USPTO requirements', duration: '2-3 days', critical: false },
    { step: 'Specimen Preparation', description: 'Gather acceptable specimens showing mark in use', duration: '1-2 days', critical: true },
    { step: 'Application Filing', description: 'Submit complete application to USPTO', duration: '1 day', critical: false },
    { step: 'USPTO Review', description: 'Examiner review (you receive confirmation within 24 hours)', duration: '3-6 months', critical: false },
    { step: 'Publication', description: 'Published for opposition period', duration: '30 days', critical: false },
    { step: 'Registration', description: 'Trademark registered and certificate issued', duration: '2-3 months', critical: false }
  ];

  const classesExplained = [
    { class: '9', category: 'Software & Electronics', examples: 'Mobile apps, computer software, electronic devices, downloadable content' },
    { class: '25', category: 'Clothing & Footwear', examples: 'Shirts, pants, shoes, hats, athletic wear, fashion accessories' },
    { class: '35', category: 'Business Services', examples: 'Retail services, advertising, marketing, business consulting, e-commerce' },
    { class: '41', category: 'Education & Entertainment', examples: 'Online courses, coaching, entertainment services, sporting events' },
    { class: '42', category: 'Technology Services', examples: 'SaaS platforms, web development, IT consulting, cloud services' },
    { class: '43', category: 'Food & Beverage Services', examples: 'Restaurants, cafes, catering, food delivery, bar services' }
  ];

  const commonMistakes = [
    { mistake: 'Wrong Classes Selected', impact: 'Incomplete protection, costly additional filings later', solution: 'We analyze your business model and select all applicable classes' },
    { mistake: 'Vague Descriptions', impact: 'Office action rejection, delays, narrower protection', solution: 'Precise, USPTO-compliant descriptions that maximize protection' },
    { mistake: 'Improper Specimens', impact: 'Application rejection, forced abandonment', solution: 'Expert guidance on acceptable specimens showing actual use' },
    { mistake: 'DIY Filing Errors', impact: '85% DIY applications receive office actions vs 45% with attorney', solution: 'Professional preparation minimizes errors and delays' }
  ];

  const faqs = [
    {
      question: 'What is the difference between "In Use" and "Intent to Use" applications?',
      answer: '"In Use" (1(a) basis): You are currently using the mark in commerce. You must provide specimens (photos, labels, screenshots) showing the mark used with your goods/services. Faster and cheaper - goes straight to registration after approval. "Intent to Use" (1(b) basis): You plan to use the mark but haven\'t started yet. Secures your filing date while you prepare for launch. Requires Statement of Use filing later with specimens and additional fees ($100 per class). Takes longer but protects your brand before market entry. Most startups file Intent to Use before product launch to secure the name.'
    },
    {
      question: 'How many classes should I file in?',
      answer: 'File in all classes where you currently offer (or will offer within 3 years) goods or services. Each class costs $350 in USPTO fees plus attorney fees. Under-filing leaves gaps in protection - competitors can register your mark in uncovered classes. Over-filing wastes money on classes you\'ll never use. Strategic approach: File core classes now (where you actively operate). Add additional classes as you expand into new product lines. Example: A coffee shop should file Class 43 (restaurant services) and Class 30 (coffee beans for retail). If selling branded merchandise later, add Class 25 (clothing).'
    },
    {
      question: 'What specimens are acceptable for USPTO?',
      answer: 'For Goods (products you sell): Labels or tags attached to the product, packaging showing the mark, product itself with mark permanently affixed, or point-of-sale displays. For Services (services you provide): Website screenshots showing services offered under the mark, advertising materials (print ads, brochures), business cards or letterhead, or signage at service location. What is NOT acceptable: mockups or digitally created images, specimens created solely for filing, invoices or internal documents, or social media posts (usually - exceptions exist). We review your specimens before filing to ensure acceptance.'
    },
    {
      question: 'What happens after I file my trademark application?',
      answer: 'Timeline after filing: Within 24 hours: Receive filing receipt and serial number. 3-6 months: Assigned to USPTO examiner for review. If approved: Published for opposition (30 days). If no opposition: Registration certificate issued (2-3 months). If office action issued: You have 6 months to respond with amendments or arguments. 90% of applications receive at least one office action. Common office actions: likelihood of confusion with existing mark, descriptiveness issues, or specimen problems. Our Professional and Premium packages include office action response assistance.'
    },
    {
      question: 'Can I file a trademark application myself?',
      answer: 'Yes, USPTO allows self-filing, but consider: Rejection rate: 85% of DIY applications receive office actions vs 45% with attorney. Time invested: Average 15-20 hours researching requirements, classes, and procedures. Cost of mistakes: Incorrect filing can lead to abandonment, requiring new application ($350+ per class wasted). Limited protection: DIY filers often select wrong classes or write narrow descriptions. Legal complexity: Trademark law has nuances (likelihood of confusion, descriptiveness, prior use) that require expertise. When to DIY: Very simple word marks, clear single class, comfortable with legal research. When to hire attorney: Logos or designs, multiple classes, high-value brands, competitive industries.'
    },
    {
      question: 'What if someone opposes my trademark?',
      answer: 'Opposition process: After approval, your mark is published in the Official Gazette for 30 days. Anyone believing they\'d be damaged by your registration can file opposition. Opposition rate: Only 3% of published marks are opposed. If opposed: You receive notice and must respond within 40 days. Opposition is similar to litigation with discovery, testimony, and legal arguments. Options: Negotiate coexistence agreement, amend application to avoid conflict, or defend in opposition proceeding. Our Premium package includes initial opposition defense strategy and consultation if opposed.'
    }
  ];

  const caseStudies = [
    {
      title: 'SaaS Company Secures Multi-Class Protection',
      challenge: 'Tech startup needed protection for software (Class 9) and services (Class 42) but on budget.',
      solution: 'Filed Professional package with strategic descriptions covering both software product and SaaS platform.',
      outcome: 'Registered in 8 months with no office actions. Later expanded to Class 35 for marketplace. Total brand value: $2M+.',
      testimonial: '"Their class strategy saved us from filing 3 separate applications. The ROI was immediate when we closed Series A."'
    },
    {
      title: 'Fashion Brand Overcomes Descriptiveness Rejection',
      challenge: 'Clothing brand name was partially descriptive of product features.',
      solution: 'Drafted disclaimer language and provided evidence of acquired distinctiveness through 3 years of sales data.',
      outcome: 'Successfully overcame office action. Mark registered with strategic disclaimer that doesn\'t limit enforcement.',
      testimonial: '"They turned what looked like a dead-end into a registered trademark. Expertise made the difference."'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="max-w-3xl">
              <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">Trademark Application Services</div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Professional <span className="text-[#168356]">Trademark Application</span> Preparation & Filing</h1>
              <p className="text-xl text-gray-300 mb-8">Expert preparation of your USPTO trademark application. Our attorneys maximize approval chances and minimize costly delays.</p>
              <div className="flex gap-4">
                <button className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">Start Your Application</button>
                <button className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">View Pricing</button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm">
                <span className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#168356]" />Attorney Prepared</span>
                <span className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#168356]" />85% Approval Rate</span>
                <span className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#168356]" />8-12 Month Registration</span>
              </div>
            </div>
          </div>
          <ServiceHeroForm title="Start Your Trademark Application" buttonText="Start Application" />
        </div>
      </div>

      {/* Statistics */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div><div className="text-4xl font-bold text-[#168356] mb-2">$350</div><div className="text-gray-600 text-sm">USPTO fee per class</div></div>
            <div><div className="text-4xl font-bold text-[#168356] mb-2">45</div><div className="text-gray-600 text-sm">International trademark classes</div></div>
            <div><div className="text-4xl font-bold text-[#168356] mb-2">85%</div><div className="text-gray-600 text-sm">DIY apps get office actions</div></div>
            <div><div className="text-4xl font-bold text-[#168356] mb-2">20yrs</div><div className="text-gray-600 text-sm">Trademark protection period</div></div>
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">What is a Trademark Application?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">The formal process of registering your brand name, logo, or slogan with the USPTO</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">A trademark application is a legal filing with the United States Patent and Trademark Office (USPTO) to register your brand identifier. Registration grants you exclusive nationwide rights to use your mark for specific goods or services.</p>
              <p className="text-gray-700 leading-relaxed">The application requires precise legal descriptions, correct class selection, and acceptable specimens showing your mark in actual use. Filing errors lead to delays, rejections, or incomplete protection that leaves your brand vulnerable.</p>
              <div className="bg-[#168356]/5 border-l-4 border-[#168356] p-6 rounded">
                <p className="font-semibold text-gray-900 mb-2">Why Professional Preparation Matters:</p>
                <p className="text-gray-700 text-sm">45% of attorney-prepared applications are approved without office actions vs 15% of DIY applications. Professional preparation saves time, money, and ensures comprehensive protection.</p>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-black mb-4">Application Components:</h3>
              {['Mark identification (word, logo, or both)', 'Filing basis (in use or intent to use)', 'International class selection', 'Precise description of goods/services', 'Specimens of use in commerce', 'Applicant information and signature', 'USPTO filing fees ($350 per class)'].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section - Using New Component */}
      <TrademarkPricingSection />

      {/* Process Timeline */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Application Process & Timeline</h2>
            <p className="text-lg text-gray-600">From filing to registration in 8-18 months</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {applicationProcess.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${item.critical ? 'bg-[#168356]' : 'bg-gray-400'}`}>{index + 1}</div>
                  {index < applicationProcess.length - 1 && <div className="w-0.5 h-16 bg-gray-300"></div>}
                </div>
                <div className="flex-1 pb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-black">{item.step}</h3>
                    {item.critical && <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full font-semibold">Critical</span>}
                  </div>
                  <p className="text-gray-700 mb-2">{item.description}</p>
                  <span className="text-sm text-[#168356] font-semibold">{item.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trademark Classes */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Common Trademark Classes</h2>
            <p className="text-lg text-gray-600">Understanding the 45 international classes</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classesExplained.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-3xl font-bold text-[#168356] mb-2">Class {item.class}</div>
                <h3 className="text-lg font-bold text-black mb-3">{item.category}</h3>
                <p className="text-sm text-gray-600">{item.examples}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">Each class costs $350 in USPTO fees. We help you select only the classes you need.</p>
          </div>
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Common Application Mistakes</h2>
            <p className="text-lg text-gray-600">Avoid these errors that lead to rejection</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {commonMistakes.map((item, index) => (
              <div key={index} className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-black mb-2">{item.mistake}</h3>
                    <p className="text-sm text-gray-700 mb-3"><strong>Impact:</strong> {item.impact}</p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                      <p className="text-sm text-gray-700"><strong className="text-green-700">Our Solution:</strong> {item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Success Stories</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div><div className="text-sm font-semibold text-gray-500 uppercase mb-2">Challenge</div><p className="text-gray-700">{study.challenge}</p></div>
                  <div><div className="text-sm font-semibold text-[#168356] uppercase mb-2">Solution</div><p className="text-gray-700">{study.solution}</p></div>
                  <div><div className="text-sm font-semibold text-green-600 uppercase mb-2">Outcome</div><p className="text-gray-700 font-semibold">{study.outcome}</p></div>
                  <div className="pt-4 border-t border-gray-200"><p className="text-sm text-gray-600 italic">{study.testimonial}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl font-bold text-black mb-4">Benefits of Trademark Registration</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4"><benefit.icon className="w-7 h-7 text-white" /></div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2></div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && <div className="px-6 pb-4 text-gray-700 leading-relaxed">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />
    </div>
  );
};

export default TrademarkApplicationPage;